import React, { useEffect, useState } from 'react';
import { useFileManagerContext } from 'modules/FileManagerV2/context/FileManagerContext';
import { Button } from 'primereact/button';
import { Dialog } from 'primereact/dialog';
import { Dropdown } from 'primereact/dropdown';
import { Tooltip } from 'primereact/tooltip';
import { presignedURLGet } from 'services/fileManager/fileManagerServices';
import { getFileClients } from 'services/fileManager/fileManagerServices';
import { toTitleCase } from 'utils/utils';
import CustomHeader from './../file/CustomHeader';
import { useHistory, useLocation } from 'react-router-dom';
import TextSnippetWrapper from 'components/TextSnippetWrapper';
import { useForm, Controller, useFieldArray } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import * as Yup from 'yup';
import { InputSwitch } from 'primereact/inputswitch';
import { uuidv4 } from 'common/uuidGenerator';
import { useUserDetailsContext } from 'context/userDetailsContext';

function SignatureModal() {
  const { modals, selectedItem, isLeadFileManager, fileManagerFilters } = useFileManagerContext();
  const { checkSubPermission } = useUserDetailsContext();

  const eSignReqScheme = Yup.object().shape({
    file_name: Yup.string().required('File name is required'),
    notes: Yup.string(),
    is_sequential: Yup.boolean(),
    assignee: Yup.array()
      .of(
        Yup.object().shape({
          assignee: Yup.string().required('Assignee is required'),
          notes: Yup.string(),
        })
      )
      .required('Assignee is required'),
  });

  const {
    control,
    handleSubmit,
    formState: { errors },
    getValues,
    watch,
    setValue,
  } = useForm({
    resolver: yupResolver(eSignReqScheme),
    defaultValues: {
      file_name: '',
      notes: '',
      is_sequential: false,
      assignee: [{ assignee: '', notes: '', id: uuidv4() }],
    },
  });

  const {
    fields,
    append,
    remove,
    watch: arrayWatch,
  } = useFieldArray({
    control,
    name: 'assignee',
  });

  const { showSignatureModal, setShowSignatureModal } = modals;
  const caseId = selectedItem?.case_id || '';

  const history = useHistory();
  const location = useLocation();

  const [filteredClientOptions, setFilteredClientOptions] = useState([]);
  const [draggingItem, setDraggingItem] = useState(null);
  const [clients, setClients] = useState([]);

  let AssigneeList = watch('assignee');

  const fetchClients = async () => {
    const response = await getFileClients({ case_id: caseId });
    setClients(response?.data || []);
  };

  useEffect(() => {
    fetchClients();
  }, [caseId]);

  useEffect(() => {
    if (selectedItem?.file_name) {
      let name = selectedItem?.file_name?.split('.');
      name.pop();
      name = name.join('.');
      setValue('file_name', name);
    }
  }, [selectedItem]);

  useEffect(() => {
    if (clients?.length > 0) {
      const option = clients
        .filter((val) => val.email)
        .map((element) => {
          const obj = {};
          const fullName = `${element.first_name ? toTitleCase(element.first_name) : ''} ${
            element.middle_name ? toTitleCase(element.middle_name) : ''
          } ${element.last_name ? toTitleCase(element.last_name) : ''}`;
          obj.is_confirmed = element.is_confirmed;
          if (element.is_create_client_portal === false && element.status !== 'ACTIVE') {
            obj.label = fullName + ' (Inactive and Disabled Lawft Access)';
          } else if (element.is_create_client_portal === false) {
            obj.label = fullName + ' (Disabled Lawft Access)';
          } else if (element.status !== 'ACTIVE') {
            obj.label = fullName + ' (Inactive)';
          } else if (element.is_confirmed && element.is_confirmed === true) {
            obj.label = fullName;
          } else if (!element.is_confirmed || element.is_confirmed === false) {
            obj.label = fullName + ' (Not Confirmed)';
          } else {
            obj.label = fullName;
          }
          obj.value = element.contact_id;
          obj.disabled = element.status !== 'ACTIVE';
          return obj;
        });
      setFilteredClientOptions(option);
      return;
    }
    setFilteredClientOptions([]);
  }, [clients]);

  useEffect(async () => {
    const fetchData = async () => {
      const file_path = selectedItem?.file_path;
      const version_id = selectedItem?.version_id;
      try {
        await presignedURLGet(file_path, version_id);
      } catch (error) {
        console.log('error', error);
      }
    };
    fetchData();
  }, [selectedItem]);

  const onSubmit = (data) => {
    const sendData = {
      file_name: data?.file_name,
      notes: data?.notes,
      client_cognito_username: data?.assignee,
      is_sequential: data?.is_sequential,
    };

    const obj = {
      case_id: selectedItem?.case_id,
      file_pk: selectedItem?.pk,
      file_sk: selectedItem?.sk,
      data: sendData,
      is_lead: isLeadFileManager,
      case_name: fileManagerFilters?.case?.case_name,
      display_name: fileManagerFilters?.case?.label,
    };

    history.push({
      pathname: '/signature-request',
      state: obj,
      search: `?to=${location?.pathname}`,
    });
  };

  const footer = (
    <div>
      <Button label="Cancel" onClick={() => setShowSignatureModal(false)} className="p-button p-button-secondary outline mx-2" />
      <Button label="Step 2 : Add Signature" onClick={() => handleSubmit(onSubmit)()} className="p-button p-button-secondary" />
    </div>
  );

  const handleDragStart = (e, index) => {
    setDraggingItem(AssigneeList[index]);
    e.dataTransfer.setData('text/plain', '');
  };

  const handleDrop = (e, targetItem) => {
    if (!draggingItem) return;
    let _assigneeList = getValues('assignee');
    const currentIndex = _assigneeList.findIndex((e) => e.assignee == draggingItem.assignee);
    const targetIndex = _assigneeList.findIndex((e) => e.assignee == targetItem.assignee);

    if (currentIndex !== -1 && targetIndex !== -1) {
      _assigneeList.splice(currentIndex, 1);
      _assigneeList.splice(targetIndex, 0, draggingItem);

      setValue('assignee', _assigneeList);
    }
  };

  let is_sequential = watch('is_sequential');
  return (
    <Dialog
      className="common-popup-style"
      header={<CustomHeader onHide={() => setShowSignatureModal(false)} title="E-sign Request" />}
      footer={footer}
      visible={showSignatureModal}
      style={{ width: '50vw' }}
      modal
      onHide={() => setShowSignatureModal(false)}
      closable={false}
    >
      <div className="w-100 h-100">
        <div className="d-flex flex-wrap h-100">
          <div className="pe-3 mt-3 col-12 d-flex flex-column">
            <div className="header fw-bolder mb-2">File Name</div>
            <Controller
              name="file_name"
              control={control}
              render={({ field }) => (
                <TextSnippetWrapper
                  value={field.value}
                  onChange={(e) => field.onChange(e.target.value)}
                  type="text"
                  rows={4}
                  cols={30}
                  autoResize
                  className="w-100 "
                  placeholder="File name"
                />
              )}
            />
            {errors.file_name && <small className="p-error">{errors.file_name?.message}</small>}
            <br />
            <div className="header fw-bolder mb-2">Any notes you want to send with the e-sign request ?</div>
            <Controller
              name="notes"
              control={control}
              render={({ field }) => (
                <TextSnippetWrapper
                  value={field.value}
                  onChange={(e) => field.onChange(e.target.value)}
                  type="textarea"
                  rows={4}
                  cols={30}
                  autoResize
                  className="border shadow-small w-100 text-message-input"
                  placeholder="Notes"
                />
              )}
            />
            {errors.notes && <small className="p-error">{errors.notes?.message}</small>}

            <br />
            {fields?.length > 1 && (
              <div className="d-flex align-items-center py-2 py-lg-0 pe-4">
                <div className="header fw-bolder me-1">Require signatures in sequence</div>
                <Tooltip target=".custom-target-icon" />

                <i
                  className="custom-target-icon pi pi-question-circle p-text-secondary p-overlay-badge  me-3 pointer"
                  data-pr-tooltip="When this option is turned on, signers will be required to sign the document one after the other in a specific order. If disabled, all signers can sign the document simultaneously without waiting for others to complete their part."
                  data-pr-position="right"
                  data-pr-at="right+5 top"
                  data-pr-my="left center-2"
                ></i>

                <Controller
                  name="is_sequential"
                  control={control}
                  render={({ field }) => <InputSwitch className="input-swich swich-w" checked={field?.value} onChange={field.onChange} />}
                />
              </div>
            )}
          </div>

          <br />

          <div className="w-100 rounded-1 pt-2 d-flex align-items-center justify-content-between mb-3">
            <div className="header fw-bolder mb-2">Recipients</div>
            <Button
              label="Add Recipients"
              onClick={() => append({ assignee: '', notes: '', id: uuidv4() })}
              className="p-button p-button-secondary"
            />
          </div>
          {fields.map((item, index) => {
            return (
              <div
                onDragStart={(e) => handleDragStart(e, index)}
                onDragEnd={() => setDraggingItem(null)}
                onDragOver={(e) => e.preventDefault()}
                onDrop={(e) => handleDrop(e, item)}
                draggable
                key={item.id}
                className="w-100 rounded-1 d-flex align-items-center mb-2 gap-2"
              >
                {fields.length > 1 && is_sequential && (
                  <div>
                    <i className="pi pi-align-justify cursor-pointer" />
                  </div>
                )}

                {/* Dropdown */}
                <div className="flex-grow-1">
                  <Controller
                    name={`assignee[${index}].assignee`}
                    control={control}
                    render={({ field }) => (
                      <Dropdown
                        value={field.value}
                        onChange={(e) => field.onChange(e.value)}
                        options={
                          checkSubPermission('esign_non_lawft')
                            ? filteredClientOptions
                            : filteredClientOptions?.filter((opt) => opt?.is_confirmed)
                        }
                        optionLabel="label"
                        placeholder="Select Contact"
                        className="w-100"
                        filter
                      />
                    )}
                  />
                  {errors.assignee && errors.assignee[index]?.assignee && (
                    <small className="p-error ms-2">{errors.assignee[index]?.assignee?.message}</small>
                  )}
                </div>

                {/* Notes */}
                {fields?.length > 1 && (
                  <div className="flex-grow-1">
                    <Controller
                      name={`assignee[${index}].notes`}
                      control={control}
                      render={({ field }) => (
                        <TextSnippetWrapper
                          value={field.value}
                          onChange={field.onChange}
                          type="text"
                          autoResize
                          className="border shadow-small w-100"
                          placeholder="Private Notes"
                        />
                      )}
                    />
                    {errors.assignee && errors.assignee[index]?.notes && (
                      <small className="p-error">{errors.assignee[index]?.notes?.message}</small>
                    )}
                  </div>
                )}

                {/* Delete icon */}
                <div className="d-flex align-items-center">
                  {fields.length !== 1 && (
                    <i
                      onClick={() => remove(index)}
                      className="icon-delete icon-red cursor-pointer"
                      severity="danger"
                      aria-label="Cancel"
                    />
                  )}
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </Dialog>
  );
}

export default SignatureModal;
